import dayjs from "dayjs";

import { axiosInstance } from "@/apis/core";
import { IS_DEMO_MODE, getDemoMonthlyReservations, demoReservations } from "@/dummy/demo";
import { vehicleDummyData } from "@/data/vehicleDummyData";
import type {
  ReservationRequest,
  ReservationStateRequest,
  ReservedPlacesRequest,
} from "@/types/reservation";
import type { Vehicle, VehicleReservation, VehicleReservationStatus } from "@/types/vehicleReservation";
import type { BatchPreviewRequest, BatchPreviewResult, BatchCreateRequest, BatchGroup, BatchDetailResponse, RejectConflictsResponse } from "@/types/batchReservation";

export const fetchReservation = async (date: string, place: string) => {
  if (IS_DEMO_MODE) {
    return demoReservations.filter(r => {
      const matchDate = r.rsvtDt === date;
      const matchPlace = place === "all" || r.plcCd === place;
      return matchDate && matchPlace;
    });
  }

  try {
    const res =
      await axiosInstance.get(`/portal-service/api/v1/reservation/list?cpsCd=PTK&rsvtDt=${date}&plcCd=${place}
`);

    return res.data;
  } catch (error) {
    console.log(error);
  }
};

export const reservation = async (reservationRequest: ReservationRequest) => {
  if (IS_DEMO_MODE) {
    console.log("Demo mode: reservation request", reservationRequest);
    return { success: true };
  }

  try {
    await axiosInstance.post(
      `/portal-service/api/v1/reservation/insert`,
      reservationRequest,
    );
  } catch (error) {
    console.log(error);
  }
};

export const putReservationState = async ({
  rsvtId,
  sttCd,
}: ReservationStateRequest) => {
  if (IS_DEMO_MODE) {
    console.log("Demo mode: putReservationState", { rsvtId, sttCd });
    return { success: true };
  }

  const endPoint = sttCd === "approve" ? "approve" : "cancel";

  try {
    await axiosInstance.put(`/portal-service/api/v1/reservation/${endPoint}`, {
      rsvtId: Number(rsvtId),
      sttCd,
    });
  } catch (error) {
    console.error(error);
  }
};

export const getReservedPlaces = async ({
  rsvtDt,
  startTime,
  endTime,
}: ReservedPlacesRequest) => {
  if (IS_DEMO_MODE) {
    return [];
  }

  try {
    const res = await axiosInstance.get(
      "/portal-service/api/v1/place/reserved/list",
      {
        params: {
          cpsCd: "PTK",
          rsvtDt,
          startTime,
          endTime,
        },
      },
    );

    return res.data;
  } catch (error) {
    console.error(error);
  }
};

export const getMonthlyReservation = async (date: string, plcCd: string) => {
  if (IS_DEMO_MODE) {
    const yearMonth = date.slice(0, 6);
    let result = getDemoMonthlyReservations(yearMonth);
    if (plcCd && plcCd !== "all") {
      result = result.map(day => ({
        ...day,
        data: day.data.filter(r => r.plcCd === plcCd),
      })).filter(day => day.data.length > 0);
    }
    return result;
  }

  try {
    const res = await axiosInstance.get(
      "/portal-service/api/v1/reservation/list/month",
      {
        params: {
          cpsCd: "PTK",
          rsvtDt: date,
          plcCd,
        },
      },
    );

    return res.data === "" ? [] : res.data;
  } catch (error) {
    console.error(error);
    return [];
  }
};

export const postReservationsByState = async (
  page: number = 0,
  size: number = 10,
  sttCd: string = "all",
  plcCd: string = "all",
  fromDate: string = dayjs().format("YYYYMMDD"),
  toDate: string = dayjs().add(30, "day").format("YYYYMMDD"),
  userName: string = "",
  sortColumn: string = "",
  sortDirection: string = "",
) => {
  if (IS_DEMO_MODE) {
    let filtered = demoReservations;
    if (sttCd && sttCd !== "all") {
      filtered = filtered.filter(r => r.sttCd === sttCd);
    }
    if (plcCd && plcCd !== "all") {
      filtered = filtered.filter(r => r.plcCd === plcCd);
    }
    filtered = filtered.filter(r => r.rsvtDt >= fromDate && r.rsvtDt <= toDate);

    const start = page * size;
    const content = filtered.slice(start, start + size);
    return {
      content,
      numberOfElements: content.length,
      totalPages: Math.ceil(filtered.length / size),
      totalElements: filtered.length,
    };
  }

  try {
    const body: Record<string, string> = {
      cpsCd: "PTK",
      sttCd,
      userId: "all",
      plcCd,
      fromDate,
      toDate,
    };
    if (userName.trim()) {
      body.userName = userName.trim();
    }
    if (sortColumn) {
      body.sortColumn = sortColumn;
    }
    if (sortDirection) {
      body.sortDirection = sortDirection;
    }

    const res = await axiosInstance.post(
      "/portal-service/api/v1/reservation/list/page",
      body,
      {
        params: {
          page,
          size,
        },
      },
    );

    return res.data;
  } catch (error) {
    console.error(error);
    return { content: [], totalElements: 0, totalPages: 0 };
  }
};

// 사용자 별 예약 목록 가져오기 (유저 본인의 장소 예약)
export const getReservationByUsers = async (
  page: number = 0,
  size: number = 10,
  userId: string = "",
  filters?: { sttCd?: string; plcCd?: string; fromDate?: string; toDate?: string },
) => {
  if (IS_DEMO_MODE) {
    let filtered = demoReservations;
    if (userId) {
      filtered = filtered.filter(r => r.mbrId === userId);
    }
    const start = page * size;
    const content = filtered.slice(start, start + size);
    return {
      content,
      numberOfElements: content.length,
      totalPages: Math.ceil(filtered.length / size),
      totalElements: filtered.length,
    };
  }

  try {
    const res = await axiosInstance.post(
      "/portal-service/api/v1/reservation/my",
      {
        cpsCd: "PTK",
        sttCd: filters?.sttCd || "all",
        plcCd: filters?.plcCd || "all",
        fromDate: filters?.fromDate,
        toDate: filters?.toDate,
      },
      {
        params: { page, size },
      },
    );

    return res.data;
  } catch (error) {
    console.log(error);
  }
};

// ============================================
// 차량 예약 API
// ============================================

// 데모 모드용 차량 목록
const DEMO_VEHICLES: Vehicle[] = [
  { vhclId: 1, vehicleName: "스타렉스 1호", vehicleNo: "78보 3232" },
  { vhclId: 2, vehicleName: "스타렉스 2호", vehicleNo: "78더 4370" },
];

// 데모 더미 예약에 차량 정보 연결 (id 홀짝으로 1호/2호 분배)
const withDemoVehicle = (r: VehicleReservation): VehicleReservation => {
  const v = r.id % 2 === 1 ? DEMO_VEHICLES[0] : DEMO_VEHICLES[1];
  return {
    ...r,
    vhclId: v.vhclId,
    vehicleName: v.vehicleName,
    vehicleNo: v.vehicleNo,
  };
};

// 사용 중인 차량 목록 조회
export const fetchVehicles = async (): Promise<Vehicle[]> => {
  if (IS_DEMO_MODE) {
    return DEMO_VEHICLES;
  }

  try {
    const res = await axiosInstance.get(
      "/portal-service/api/v1/vehicle-reservation/vehicles",
      { params: { cpsCd: "PTK" } },
    );
    return Array.isArray(res.data) ? res.data : [];
  } catch (error) {
    console.error(error);
    return [];
  }
};

// ── 차량(마스터) 관리 (관리자 전용) ──────────────────────────
export const createVehicle = async (data: {
  vehicleName: string;
  vehicleNo: string;
}): Promise<Vehicle> => {
  if (IS_DEMO_MODE) {
    console.log("Demo mode: createVehicle", data);
    return { vhclId: Date.now(), ...data };
  }
  const res = await axiosInstance.post(
    "/portal-service/api/v1/vehicle-reservation/vehicles",
    data,
  );
  return res.data;
};

export const updateVehicle = async (
  vhclId: number,
  data: { vehicleName: string; vehicleNo: string },
): Promise<Vehicle> => {
  if (IS_DEMO_MODE) {
    console.log("Demo mode: updateVehicle", { vhclId, ...data });
    return { vhclId, ...data };
  }
  const res = await axiosInstance.put(
    `/portal-service/api/v1/vehicle-reservation/vehicles/${vhclId}`,
    data,
  );
  return res.data;
};

export const deleteVehicle = async (vhclId: number): Promise<void> => {
  if (IS_DEMO_MODE) {
    console.log("Demo mode: deleteVehicle", vhclId);
    return;
  }
  await axiosInstance.delete(
    `/portal-service/api/v1/vehicle-reservation/vehicles/${vhclId}`,
  );
};

// 월별 차량 예약 목록 조회
export const fetchVehicleReservations = async (
  rsvtDt: string, // YYYYMM
  sttCd: string = "all",
): Promise<VehicleReservation[]> => {
  if (IS_DEMO_MODE) {
    const yearMonth = `${rsvtDt.substring(0, 4)}-${rsvtDt.substring(4, 6)}`;
    let filtered = vehicleDummyData.filter(r =>
      r.reservationDate.startsWith(yearMonth),
    );
    if (sttCd && sttCd !== "all") {
      filtered = filtered.filter(r => r.status === sttCd);
    }
    return filtered.map(withDemoVehicle);
  }

  try {
    const res = await axiosInstance.get(
      "/portal-service/api/v1/vehicle-reservation/list",
      {
        params: { cpsCd: "PTK", rsvtDt, sttCd },
      },
    );
    return res.data;
  } catch (error) {
    console.error(error);
    return [];
  }
};

// 일별 차량 예약 조회 (홈 화면용)
export const fetchVehicleReservationsByDate = async (
  rsvtDt: string, // YYYYMMDD
): Promise<VehicleReservation[]> => {
  if (IS_DEMO_MODE) {
    const formatted = `${rsvtDt.substring(0, 4)}-${rsvtDt.substring(4, 6)}-${rsvtDt.substring(6, 8)}`;
    return vehicleDummyData
      .filter(r => r.reservationDate === formatted)
      .map(withDemoVehicle);
  }

  try {
    const res = await axiosInstance.get(
      "/portal-service/api/v1/vehicle-reservation/daily",
      {
        params: { cpsCd: "PTK", rsvtDt },
      },
    );
    return res.data;
  } catch (error) {
    console.error(error);
    return [];
  }
};

// 나의 차량 예약 조회 (마이페이지/대시보드용)
export const fetchMyVehicleReservations = async (
  _mbrId?: string,
): Promise<VehicleReservation[]> => {
  if (IS_DEMO_MODE) {
    return vehicleDummyData.map(withDemoVehicle);
  }

  try {
    const res = await axiosInstance.get(
      "/portal-service/api/v1/vehicle-reservation/my",
      { params: { cpsCd: "PTK" } },
    );
    return Array.isArray(res.data) ? res.data : [];
  } catch (error) {
    console.error(error);
    return [];
  }
};

// 차량 예약 생성
export const createVehicleReservation = async (data: {
  vhclId: number; // 사용자가 선택한 차량
  mbrId: string;
  applicantName: string;
  applicantPhone: string;
  communityName: string;
  rsvtDt: string; // YYYYMMDD
  drivingTime: string;
  purpose: string;
  destination: string;
  driverName: string;
  driverPhone: string;
}) => {
  if (IS_DEMO_MODE) {
    console.log("Demo mode: createVehicleReservation", data);
    return { success: true };
  }

  try {
    const res = await axiosInstance.post(
      "/portal-service/api/v1/vehicle-reservation/insert",
      {
        cpsCd: "PTK",
        ...data,
      },
    );
    return res.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

// 차량 예약 상태 변경 (승인/반려/취소)
export const updateVehicleReservationStatus = async (
  vhclRsvtId: number,
  sttCd: VehicleReservationStatus,
  rejectReason?: string,
) => {
  if (IS_DEMO_MODE) {
    console.log("Demo mode: updateVehicleReservationStatus", { vhclRsvtId, sttCd, rejectReason });
    return { success: true };
  }

  try {
    const res = await axiosInstance.put(
      "/portal-service/api/v1/vehicle-reservation/status",
      {
        vhclRsvtId,
        sttCd,
        rejectReason,
      },
    );
    return res.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

// ============================================
// 일괄 예약 API
// ============================================

// 일괄 예약 미리보기 (충돌 체크)
export const batchPreview = async (data: BatchPreviewRequest): Promise<BatchPreviewResult[]> => {
  try {
    const res = await axiosInstance.post(
      "/portal-service/api/v1/reservation/batch/preview",
      data,
    );
    return res.data;
  } catch (error) {
    console.error(error);
    return [];
  }
};

// 일괄 예약 생성
export const batchCreate = async (data: BatchCreateRequest): Promise<any> => {
  try {
    const res = await axiosInstance.post(
      "/portal-service/api/v1/reservation/batch/create",
      data,
    );
    return res.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

// 일괄 예약 그룹 목록 조회
export const fetchBatchList = async (): Promise<BatchGroup[]> => {
  try {
    const res = await axiosInstance.get(
      "/portal-service/api/v1/reservation/batch/list",
      { params: { cpsCd: "PTK" } },
    );
    return res.data;
  } catch (error) {
    console.error(error);
    return [];
  }
};

// 일괄 예약 그룹 상세 조회
export const fetchBatchDetail = async (batchId: number): Promise<BatchDetailResponse | null> => {
  try {
    const res = await axiosInstance.get(
      "/portal-service/api/v1/reservation/batch/detail",
      { params: { batchId } },
    );
    return res.data;
  } catch (error) {
    console.error(error);
    return null;
  }
};

// 일괄 예약 기간과 겹치는 기존 예약 일괄 반려 (관리자 전용)
// 실패를 호출부에서 사용자에게 알려야 하므로 에러를 삼키지 않고 throw 한다.
export const rejectConflictReservations = async (
  rsvtIds: number[],
): Promise<RejectConflictsResponse> => {
  const res = await axiosInstance.put(
    "/portal-service/api/v1/reservation/batch/reject-conflicts",
    { rsvtIds },
  );
  return res.data;
};

// 일괄 예약 전체 취소
export const cancelBatch = async (batchId: number): Promise<any> => {
  try {
    const res = await axiosInstance.put(
      "/portal-service/api/v1/reservation/batch/cancel",
      { batchId },
    );
    return res.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};
